import React, { useState } from 'react';
import { 
    Box, 
    CircularProgress, 
    IconButton, 
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography 
} from '@mui/material';
import ResponsiveTooltip from '../../ResponsiveTooltip';
import { useTranslation } from 'react-i18next';
import DescriptionIcon from '@mui/icons-material/Description';
import { usePaymentQueries } from '../../../hooks/usePaymentQueries';
import { useNotification } from '../../../context/NotificationContext';

interface Invoice {
    id: string;
    paymentIntentId: string;
    chargeId: string;
    amount: number;
    currency: string;
    date: string;
    description: string;
    invoiceUrl?: string;
    receiptUrl?: string;
    isRefund?: boolean;
    isAffiliate?: boolean;
    status: string;
}

const InvoiceHistorySection: React.FC = () => {
    const { t } = useTranslation();
    const { invoices = [], isLoadingInvoices, invoicesError, getInvoice } = usePaymentQueries();
    const { showNotification } = useNotification();
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);

    const formatAmount = (amount: number, currency: string, isRefund: boolean = false) => {
        // For refunds, make the value negative to display with a minus sign
        const value = (isRefund ? -1 : 1) * (amount / 100); // Convert cents to base currency unit
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(value);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
    };

    const handleDownloadRegularInvoice = (invoice: Invoice) => {
        try {
            setDownloadingInvoiceId(invoice.id);
            
            if (invoice.invoiceUrl) {
                window.open(invoice.invoiceUrl, '_blank');
            } else {
                handleDownloadViaAPI(invoice.paymentIntentId, false);
            }
        } catch (error: any) {
            console.error('Error opening invoice URL:', error);
            showNotification(t('settings.plan.errors.invoiceError'), 'error');
        } finally {
            setDownloadingInvoiceId(null);
        }
    };
    
    const handleDownloadRefundReceipt = (invoice: Invoice) => {
        try {
            setDownloadingInvoiceId(invoice.id);
            handleDownloadViaAPI(invoice.chargeId, true);
        } catch (error: any) {
            console.error('Error initiating refund download:', error);
            showNotification(t('settings.plan.errors.invoiceError'), 'error');
        } finally {
            setDownloadingInvoiceId(null);
        }
    };
    
    const handleDownloadViaAPI = async (paymentId: string, isRefund: boolean) => {
        try {
            if (!paymentId) {
                showNotification(t('settings.plan.errors.noInvoiceFound'), 'warning');
                return;
            }
            
            const data = await getInvoice(paymentId, isRefund);
            
            if (data.invoiceUrl) {
                window.open(data.invoiceUrl, '_blank');
            } else {
                showNotification(t('settings.plan.errors.noInvoiceFound'), 'warning');
            }
        } catch (error: any) {
            console.error('Error downloading via API:', error);
            showNotification(t('settings.plan.errors.invoiceError'), 'error');
        }
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 3,
                bgcolor: 'background.paper',
            }}
        >
            <Typography variant="h6" fontWeight="bold" fontSize="1.25rem"
            sx={{ 
                mb: 2
            }}
            >
                {t('settings.plan.invoiceHistory')}
            </Typography>
            {isLoadingInvoices ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 2,
                    }}
                >
                    <CircularProgress size={24} />
                </Box>
            ) : invoices && invoices.length > 0 ? (
                <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0, py: 1, '&:last-child': { pr: 0 } } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('settings.plan.date')}</TableCell>
                            <TableCell>{t('settings.plan.amount')}</TableCell>
                            <TableCell align="right" sx={{ width: '48px' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoices.map((invoice: Invoice) => (
                            <TableRow key={invoice.id}>
                                <TableCell>
                                    {formatDate(invoice.date)}
                                    {invoice.isRefund && (
                                        <Typography 
                                            variant="body2" 
                                            color="error" 
                                            component="span"
                                            sx={{ 
                                                ml: 1,
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            ({t('settings.plan.refunded')})
                                        </Typography>
                                    )}
                                    {invoice.isAffiliate && (
                                        <Typography 
                                            variant="body2" 
                                            color="success" 
                                            component="span"
                                            sx={{ 
                                                ml: 1,
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            ({t('settings.plan.affiliate')})
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>{formatAmount(invoice.amount, invoice.currency, invoice.isRefund)}</TableCell>
                                <TableCell align="right">
                                    {invoice.paymentIntentId && (
                                        <ResponsiveTooltip title={invoice.isRefund ? t('settings.plan.buttons.refundInvoice') : t('settings.plan.buttons.invoice')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => invoice.isRefund ? handleDownloadRefundReceipt(invoice) : handleDownloadRegularInvoice(invoice)}
                                                disabled={downloadingInvoiceId === invoice.id}
                                            >
                                                <DescriptionIcon fontSize="small" />
                                            </IconButton>
                                        </ResponsiveTooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem', p: 0 }}>
                    {t('settings.plan.noPaymentHistory')}
                </Typography>
            )}
        </Paper>
    );
};

export default InvoiceHistorySection;
